/**
 * Copyright (c) 2026 Moverick. All rights reserved.
 */
import Vue from 'vue'
import {mapGetters, mapActions} from 'vuex'
import {userPositionMarker} from 'js/lib/planner'
import TopologyStopsService from 'js/services/api/topology/stops.js'
import TopologyRoutesService from 'js/services/api/topology/routes.js'
import MapMixin from 'js/mixins/map.js'
import VueBarMixin from 'js/mixins/vue-bar.js'
import StopIndexContent from 'js/components/stops-index/content'
import StopShowContent from 'js/components/stop-show/content'
import StopShowHeader from 'js/components/stop-show/header'
import GeojsonMixin from 'js/mixins/routes-geojson-mixin'
import MarkersMixin from 'js/mixins/stops-markers-mixin'
import SidepanelMixin from 'js/mixins/stops-sidepanel-mixin'
import template from './index.pug'

/** Retardo en ms para el debounce de recarga de paradas y redimensionado del mapa. */
const DEBOUNCE_DELAY = 500

/** Duración en ms de la animación de centrado del mapa. */
const MAP_ANIMATION_MS = 500

/** Intervalo en ms del polling que espera la inicialización del mapa. */
const POLL_INTERVAL_MS = 100

/** Ajuste de distancia para el cálculo de paradas cercanas. */
const DISTANCE_ADJUSTMENT = 0.7

// @vue/component
export default Vue.extend({
  name: 'nearest-stops-page',
  template: template(),

  components: {
    StopShowContent,
    StopIndexContent,
    StopShowHeader
  },

  mixins: [MapMixin, VueBarMixin, GeojsonMixin, MarkersMixin, SidepanelMixin],

  data() {
    return {
      /** @type {Array<Object>} Paraderos cargados desde la API. */
      stops: [],
      /** @type {Array<Object>} Operadores actualmente seleccionados en el filtro. */
      selectedOperators: [],
      /** @type {Array<Object>} Rutas del municipio cargadas desde la API. */
      routes: [],
      /** @type {Object} Paradero actualmente seleccionado. */
      selectedStop: {},
      /** @type {Object} Centro actual del mapa en coordenadas lat/lng. */
      pointerPosition: {},
      /** @type {boolean} Indica si las paradas están siendo cargadas. */
      stopsLoading: false,
      /** @type {number|null} Referencia al timeout del debounce de recarga. */
      stopsTimeout: null,
      /** @type {Array<Object>} Paradas visibles tras aplicar filtros de operador y distancia. */
      filteredStops: [],
      /** @type {boolean} Controla la visibilidad del modal de selección de operadores. */
      showModal: false,
      /** @type {string|null} ID de la parada actualmente seleccionada. */
      selectedStopId: null,
      /** @type {number|null} Radio de búsqueda en metros desde el centro del mapa. */
      distance: null,
      /**
       * Reglas de límite de paradas por zoom.
       * Los tenants pueden sobreescribir estas reglas desde su data().
       */
      stopLimitRulesByZoom: [
        {maxZoom: 12, limit: 20},
        {maxZoom: 13, limit: 40},
        {maxZoom: 14, limit: 80},
        {maxZoom: 15, limit: 140},
        {maxZoom: 16, limit: 220}
      ],
      /** Límite por defecto cuando no aplica ninguna regla. */
      stopLimitDefault: Infinity
    }
  },

  computed: {
    ...mapGetters('appConfig', ['hasBasicData']),
    ...mapGetters(['userPosition', 'operators']),

    /**
     * Icono para marcar la posición del usuario en el mapa.
     * @returns {Object} Configuración del icono Leaflet.
     */
    userIcon() {
      return userPositionMarker
    },

    /**
     * Paradas visibles en el mapa tras aplicar filtros.
     * Consumido por MarkersMixin para renderizar marcadores.
     * @returns {Array<Object>} Lista de paradas filtradas.
     */
    mapFilteredStops() {
      return this.getVisibleStopsByMapAndZoom(this.filteredStops)
    },

    /**
     * Indica si hay una parada seleccionada actualmente.
     * @returns {boolean}
     */
    isStopSelected() {
      return !_.isEmpty(this.selectedStop)
    }
  },

  watch: {
    $municipality: {
      /**
       * Reacciona al cambio de municipio cargando su configuración inicial de mapa.
       * @param {Object} value - Datos del municipio seleccionado.
       */
      handler(value) {
        if(value){
          this.setMunicipalityConfig(value)
        }
      },
      immediate: true
    },

    pointerPosition: {
      /**
       * Recarga las paradas cercanas cada vez que cambia el centro del mapa.
       * Oculta el tooltip si no hay ninguna parada seleccionada.
       */
      handler() {
        this.reloadNearestsStop()
      }
    },

    /**
     * Invalida el tamaño del mapa al abrir o cerrar el sidepanel
     * para evitar que quede con un área de render incorrecta.
     */
    sidePanelOpen() {
      setTimeout(() => this.map.invalidateSize(true), DEBOUNCE_DELAY)
    }
  },

  mounted() {
    this.initializePage()

    this.$nextTick(() => {
      const waitForMap = setInterval(() => {
        if(this.map){
          clearInterval(waitForMap)
          this.map.whenReady(this.onMapReady)
        }
      }, POLL_INTERVAL_MS)
    })
  },

  beforeDestroy() {
    clearTimeout(this.stopsTimeout)

    if(this.map){
      this.map.off('zoomend')
      this.map.off('moveend')
    }
  },

  methods: {
    ...mapActions(['entryPointAction', 'getOperators']),

    // =============================================
    // INICIALIZACIÓN
    // =============================================

    /**
     * Configura los listeners del mapa tras su inicialización.
     * Llamado por whenReady para evitar anidamiento excesivo de funciones.
     */
    onMapReady() {
      this.updateDistanceFromMap()
      this.reloadNearestsStop()
      this.map.on('zoomend', this.onZoomEnd)
      this.map.on('moveend', this.onMoveEnd)
    },

    /** Actualiza distancia y recarga paradas al hacer zoom. */
    onZoomEnd() {
      this.updateDistanceFromMap()
      this.reloadNearestsStop()
    },

    /**
     * Actualiza el centro del mapa y la distancia al mover el mapa.
     * La recarga de paradas la dispara el watcher de pointerPosition.
     */
    onMoveEnd() {
      this.pointerPosition = this.map.getCenter()
      this.updateDistanceFromMap()
    },

    /**
     * Punto de entrada del ciclo de vida de la página.
     * Aplica contraste, carga la configuración de la app y los datos iniciales.
     * @returns {Promise<void>}
     */
    async initializePage() {
      this.cargarContraste()

      if(!this.currentAppConfig || Object.keys(this.currentAppConfig).length === 0){
        await this.$store.dispatch('appConfig/loadAppConfig')
      }

      if(this.currentAppConfig && Object.keys(this.currentAppConfig).length > 0){
        await this.loadData()
      } else {
        // eslint-disable-next-line no-console
        console.error('No se pudo cargar la configuración de la app.')
      }
    },

    /**
     * Carga todos los datos iniciales de la página:
     * rutas, operadores y estaciones de bicicleta (solo para VL).
     * @returns {Promise<void>}
     */
    async loadData() {
      await this.getRoutes()
      await this.getOperators()
      this.applyOperators()

      await this.loadTenantData()
    },

    /**
     * Configura el centro del mapa y el nivel de zoom inicial
     * a partir de los datos geográficos del municipio seleccionado.
     * @param {Object} municipality - Datos del municipio con `geo` y `stops_map_zoom`.
     */
    setMunicipalityConfig(municipality) {
      this.pointerPosition = municipality.geo
      this.zoom = municipality.stops_map_zoom

      this.$nextTick(() => {
        this.updateDistanceFromMap()
      })
    },

    // =============================================
    // MAPA
    // =============================================

    /**
     * Calcula y actualiza el radio de búsqueda en metros midiendo la distancia
     * desde el centro del mapa hasta la esquina noreste del viewport actual.
     * Ajuste de la distancia para aproximar mejor el número de paradas visibles al esperado.
     */
    updateDistanceFromMap() {
      if(!this.map){
        return
      }

      const bounds = this.map.getBounds()
      const center = this.map.getCenter()
      const northEast = bounds.getNorthEast()

      this.distance = center.distanceTo(northEast)
      this.distance *= DISTANCE_ADJUSTMENT
    },

    /**
     * Actualiza `pointerPosition` con el centro actual del mapa.
     * Se invoca al mover el mapa manualmente desde controles externos.
     */
    changePosition() {
      this.pointerPosition = this.map.getCenter()
    },

    /**
     * Centra el mapa en las coordenadas indicadas manteniendo el zoom actual.
     * Resuelve la promesa tras completarse la animación de desplazamiento.
     * @param {number} lat - Latitud del destino.
     * @param {number} lng - Longitud del destino.
     * @returns {Promise<void>}
     */
    centerMapOnMarker(lat, lng) {
      return new Promise(resolve => {
        const zoomLevel = this.map.getZoom()

        this.map.setView([lat, lng], zoomLevel, {
          animate: true,
          duration: 0.5
        })

        setTimeout(resolve, MAP_ANIMATION_MS)
      })
    },

    // =============================================
    // PARADAS
    // =============================================

    /**
     * Dispara la recarga de paradas cercanas con un debounce de 500ms.
     * Requiere que el municipio, el mapa y la distancia estén disponibles.
     */
    reloadNearestsStop() {
      if(!this.$municipality || !this.map){
        return
      }

      if(!this.distance) {
        this.updateDistanceFromMap()
      }

      if(!this.distance){
        return
      }

      clearTimeout(this.stopsTimeout)

      this.stopsTimeout = setTimeout(() => {
        this.getNearestsStops()
      }, DEBOUNCE_DELAY)
    },

    /**
     * Obtiene las paradas más cercanas al centro del mapa desde la API,
     * las normaliza, procesa las estaciones de bicicleta, aplica operadores
     * y ordena el resultado por distancia ascendente.
     * @returns {Promise<void>}
     */
    async getNearestsStops() {
      if(
        !this.$municipality?.administrative_area_id ||
        !this.pointerPosition?.lat ||
        !this.pointerPosition?.lng ||
        !this.distance
      ){
        return
      }

      this.stopsLoading = true

      try {
        const options = {
          administrative_area_id: this.$municipality.administrative_area_id,
          municipality_id: this.$municipality.id,
          coordinates: [this.pointerPosition.lng, this.pointerPosition.lat],
          distance: Math.round(this.distance)
        }

        const response = await TopologyStopsService.nearests(options, true)

        this.stops = this.manageStops(response.data)
        this.stops = this.filterStopsByOperatorRoutes()
        this.cargarContraste()
        await this.processSpecialStops()
        this.applyOperators()
        this.sortStopsByDistance()
        this.checkSelectedStop()
      } finally {
        this.stopsLoading = false
      }
    },

    /**
     * Normaliza los objetos de parada mapeando `_id` a `id`
     * sin mutar los objetos originales.
     * @param {Array<Object>} stops - Array de paradas recibidas desde la API.
     * @returns {Array<Object>} Paradas normalizadas con la propiedad `id`.
     */
    manageStops(stops) {
      // eslint-disable-next-line no-underscore-dangle
      return stops.map(stop => ({...stop, id: stop._id}))
    },

    /**
     * Comprueba si la parada seleccionada sigue estando en `filteredStops`.
     * Si no se encuentra, la deselecciona para volver al listado.
     */
    checkSelectedStop() {
      const stop = this.filteredStops.find(s => s.id === this.selectedStop.id)

      if(!stop){
        this.hideStop()
      }
    },

    /**
     * Ordena `filteredStops` por distancia ascendente respecto al centro del mapa,
     * descartando las paradas que superen el radio visible.
     */
    sortStopsByDistance() {
      if(!this.pointerPosition?.lat || !this.pointerPosition?.lng || !this.map){
        // eslint-disable-next-line no-console
        console.warn('Posición del usuario no disponible.')

        return
      }

      this.filteredStops = this.filteredStops
        .map(stop => ({
          ...stop,
          distance: this.map.distance(
            [this.pointerPosition.lat, this.pointerPosition.lng],
            [stop.lat, stop.lng]
          )
        }))
        .filter(stop => stop.distance <= this.distance)
        .sort((a, b) => a.distance - b.distance)

      this.applyMapFilteredStops()
    },

    // =============================================
    // SELECCIÓN DE PARADA
    // =============================================

    /**
     * Gestiona la selección de una parada: centra el mapa, actualiza el estado
     * en Vuex, abre el sidepanel y muestra el tooltip si es una estación de bicicleta.
     * Si la parada ya estaba seleccionada, la deselecciona.
     * @param {Object} stop - Objeto de la parada seleccionada.
     * @returns {Promise<void>}
     */
    async showStop(stop) {
      this.beforeShowStop(stop)
      await this.centerMapOnMarker(stop.lat, stop.lng)

      if(this.selectedStopId === stop.id){
        this.hideStop()
        this.selectedStopId = null
        this.resetRoutesToShow()

        return
      }

      this.selectedStop = {...stop}
      this.selectedStopId = stop.id

      this.entryPointAction({
        previousLocation: 'map',
        sublineId: '',
        routeId: '',
        previousIsFavorites: false
      })

      this.$store.commit('setStop', stop)

      this.onStopSelected(stop)

      this.sidePanelOpen = true
      this.drawStopRoutes(stop)
    },

    /**
     * Deselecciona la parada actual y elimina las rutas mostradas en el mapa.
     */
    hideStop() {
      this.selectedStop = {}
      this.resetRoutesToShow()
      this.onStopHidden()
    },

    /**
     * Solicita a la API las rutas activas de una parada y las asigna
     * a `routesToShow` para su representación visual en el mapa.
     * @param {Object} stop - Parada cuyas rutas se quieren dibujar.
     */
    drawStopRoutes(stop) {
      const administrativeAreaId = this.$municipality.administrative_area_id

      if(!administrativeAreaId){
        return
      }

      TopologyRoutesService.index({
        administrative_area_id: administrativeAreaId,
        stop_id: stop.id,
        active: 1,
        per: 0
      }).then(response => {
        this.routesToShow = response.data
      })
    },

    /**
     * Elimina todas las rutas mostradas actualmente en el mapa.
     */
    resetRoutesToShow() {
      this.routesToShow = []
    },

    /**
     * Hook para lógica personalizada de tenant tras la carga inicial.
     * Core no añade lógica extra por defecto.
     */
    async loadTenantData() {},

    /** Hook para enriquecer las paradas tras cada carga. */
    async processSpecialStops() {},

    /**
     * Hook invocado al iniciar la selección de una parada.
     */
    beforeShowStop() {},

    /** Hook invocado tras seleccionar una parada. */
    onStopSelected() {},

    /**
     * Hook invocado al ocultar la parada seleccionada.
     */
    onStopHidden() {},

    /**
     * Devuelve el límite máximo de paradas visibles según el nivel de zoom.
     * @param {number} zoom - Zoom actual del mapa.
     * @returns {number} Límite máximo de paradas a mostrar.
     */
    getDynamicStopLimitByZoom(zoom) {
      if (!Number.isFinite(zoom)) {
        return this.stopLimitDefault
      }

      const rules = Array.isArray(this.stopLimitRulesByZoom)
        ? this.stopLimitRulesByZoom
        : []
      const matchedRule = rules.find(rule =>
        Number.isFinite(rule?.maxZoom) && zoom <= rule.maxZoom
      )
      if (matchedRule && Number.isFinite(matchedRule.limit)) {
        return matchedRule.limit
      }

      return this.stopLimitDefault
    },

    /**
     * Devuelve el tamaño de celda para espaciar marcadores según el zoom.
     * @param {number} zoom - Zoom actual del mapa.
     * @returns {number} Tamaño de celda en grados.
     */
    getCellSizeByZoom(zoom) {
      if (!Number.isFinite(zoom)) {
        return 0.01
      }

      if (zoom <= 12) return 0.03
      if (zoom <= 13) return 0.02
      if (zoom <= 14) return 0.015
      if (zoom <= 15) return 0.01
      if (zoom <= 16) return 0.007

      return 0.01
    },

    /**
     * Punto de extensión para recorte/filtrado adicional en personalizaciones.
     * Por defecto aplica un recorte por bounds/zoom para evitar nubes de iconos.
     */
    getVisibleStopsByMapAndZoom(inputStops = this.filteredStops) {
      const stops = Array.isArray(inputStops) ? inputStops : []
      if (!stops.length) {
        return stops
      }

      let visibleStops = stops
      if (this?.map?.getBounds) {
        const bounds = this.map.getBounds()
        visibleStops = stops.filter(stop => {
          const lat = Number(stop.lat)
          const lng = Number(stop.lng)
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
          return bounds.contains([lat, lng])
        })
      }

      const zoom = this.map && this.map.getZoom ? this.map.getZoom() : this.zoom
      const limit = this.getDynamicStopLimitByZoom(zoom)
      const center = this.map && this.map.getCenter ? this.map.getCenter() : this.pointerPosition
      const hasCenter = center && Number.isFinite(center.lat) && Number.isFinite(center.lng)

      if (hasCenter) {
        visibleStops = visibleStops.slice().sort((a, b) => {
          const aLat = Number(a.lat)
          const aLng = Number(a.lng)
          const bLat = Number(b.lat)
          const bLng = Number(b.lng)
          const da = (aLat - center.lat) ** 2 + (aLng - center.lng) ** 2
          const db = (bLat - center.lat) ** 2 + (bLng - center.lng) ** 2
          return da - db
        })
      }

      if (Number.isFinite(limit)) {
        const cellSize = this.getCellSizeByZoom(zoom)
        const seenCells = new Set()
        const spacedStops = []

        for (const stop of visibleStops) {
          const lat = Number(stop.lat)
          const lng = Number(stop.lng)
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue

          const cellX = Math.floor(lng / cellSize)
          const cellY = Math.floor(lat / cellSize)
          const cellKey = `${cellX}:${cellY}`
          if (seenCells.has(cellKey)) continue

          seenCells.add(cellKey)
          spacedStops.push(stop)

          if (spacedStops.length >= limit) break
        }

        visibleStops = spacedStops
      }

      if (this.selectedStop && this.selectedStop.id) {
        const alreadyVisible = visibleStops.some(stop => stop.id === this.selectedStop.id)
        if (!alreadyVisible) {
          const selected = stops.find(stop => stop.id === this.selectedStop.id)
          if (selected) visibleStops = [selected, ...visibleStops]
        }
      }

      return visibleStops
    },

    /**
     * Aplica filtrado extra de mapa/zoom si la personalización lo implementa.
     */
    applyMapFilteredStops() {
      this.filteredStops = this.getVisibleStopsByMapAndZoom(this.filteredStops)
    },

    // =============================================
    // OPERADORES
    // =============================================

    /**
     * Obtiene todas las rutas del municipio actual con el template de paradas.
     * @returns {Promise<void>}
     */
    getRoutes() {
      return TopologyRoutesService.index({
        municipality_id: this.$municipality.id,
        per: 0,
        template: 'stops'
      }).then(response => {
        this.routes = response.data
      })
    },

    /**
     * Filtra el array `stops` para incluir solo las paradas que pertenecen
     * a alguno de los operadores disponibles, asignando logo e información
     * del operador a cada parada resultante.
     * @returns {Array<Object>} Paradas filtradas con metadatos de operador asignados.
     */
    filterStopsByOperatorRoutes() {
      const operators = Array.isArray(this.operators) ? this.operators : []
      const stops = Array.isArray(this.stops) ? this.stops : []
      const normalizeId = value => (value === null || value === undefined ? null : String(value))

      const allOperatorRouteIds = new Set(
        operators.flatMap(
          operator => (
            Array.isArray(operator.route_ids)
              ? operator.route_ids.map(normalizeId).filter(Boolean)
              : []
          )
        )
      )

      const filteredStops = stops.filter(stop =>
        Array.isArray(stop.route_ids) &&
        stop.route_ids.some(routeId => allOperatorRouteIds.has(normalizeId(routeId)))
      )

      filteredStops.forEach(stop => {
        const operator = operators.find(ope =>
          Array.isArray(ope.route_ids) &&
          Array.isArray(stop.route_ids) &&
          ope.route_ids.some(id =>
            stop.route_ids.map(normalizeId).includes(normalizeId(id))
          )
        )

        const logoUrl =
          operator?.shadow_logo_url?.url ||
          operator?.logo_url?.url ||
          operator?.img_url?.url ||
          null

        stop.logo_url = logoUrl ? [logoUrl] : []
        stop.operator_ids = operator ? [operator.id] : []
        stop.operator = operator
        stop.operator_id = operator?.id ?? null
      })

      return filteredStops
    },

    /**
     * Aplica el filtro de operadores seleccionados sobre las paradas cargadas,
     * forzando la reactividad de Vue al asignar un nuevo array.
     */
    applyOperators() {
      const operators = Array.isArray(this.operators) ? this.operators : []
      const stops = Array.isArray(this.stops) ? this.stops : []
      const normalizeId = value => (value === null || value === undefined ? null : String(value))

      this.selectedOperators = operators.filter(operator => operator.selected)
      this.showModal = false

      const selectedOperatorIds = new Set(
        this.selectedOperators.map(operator => normalizeId(operator.id)).filter(Boolean)
      )

      this.filteredStops = stops.filter(stop => {
        const hasOperatoIds = Array.isArray(stop.operator_ids)
        const someSelectedOperatorId = hasOperatoIds &&
          stop.operator_ids.some(opId => selectedOperatorIds.has(normalizeId(opId)))

        return hasOperatoIds && someSelectedOperatorId
      })

      this.applyMapFilteredStops()
    },

    /**
     * Alterna la visibilidad del modal de selección de operadores.
     */
    toggleModal() {
      this.showModal = !this.showModal
    },

    // =============================================
    // CONTRASTE
    // =============================================

    /**
     * Ejecuta `applyContrasteCss` con un retardo de 1 segundo para asegurar
     * que el DOM esté completamente renderizado antes de modificar estilos.
     */
    cargarContraste() {
      setTimeout(() => {
        this.applyContrasteCss()
      }, 1000)
    },

    /* eslint-disable max-len */
    /**
     * Aplica o revierte los estilos de alto contraste sobre los elementos globales de la UI.
     * Utiliza jQuery para modificar directamente el CSS de múltiples selectores.
     * Se activa en función de la propiedad `this.contraste`.
     *
     * @remarks
     * Este método utiliza jQuery por compatibilidad con el sistema de contraste global.
     * La refactorización hacia clases CSS dinámicas de Vue queda pendiente como mejora futura.
     */
    applyContrasteCss() {
      const colorWhiteImportant = 'color: #ffffff !important'
      const colorDarkImportant = 'color: #1e1e1e !important'
      const backgroundColorRule = 'background-color'

      const ruleClass1 = '.ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop'
      const ruleClass2 = '.text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu'
      const ruleClass3 = '.sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column'
      const ruleClass4 = '#iconorestablecer'
      const ruleClass5 = '#textorestablecer'

      const backgroundColorDarkImportant = 'background-color: #1e1e1e !important'

      if(this.contraste){
        $('.ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop ,.flex-middle,.ns-layout__footer__footer').css('cssText', colorWhiteImportant)
        $('.ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop').css('background-color', '#1e1e1e')
        $('.text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu').css('cssText', colorWhiteImportant)
        $('.text-primary , .text-gray2-hover , .button-phone , .text-gray3 ').css(backgroundColorRule, '#1e1e1e')
        $('a ,.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items , .pb-d6 , .sidepanel-map__card--item__point-name').css('color', '#fff')
        $('.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items ,.pb-d6, .sidepanel-map__card--item__point-name,.flex-middle ,.sidepanel-map__header').css('cssText', backgroundColorDarkImportant)
        $(ruleClass3).css('color', '#fff')
        $().css(backgroundColorRule, '#1e1e1e')
        $('label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title ').css('color', '#fff')
        $('label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title,.colorFondo ').css(backgroundColorRule, '#1e1e1e')
        $('.ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube').css('color', '#1e1e1e')
        $('.ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube').css(backgroundColorRule, '#fff')
        $(' .pb-d6 ').css('color', '#fff')
        $(ruleClass4).css('color', '#1e1e1e')
        $(ruleClass4).css(backgroundColorRule, '#fff')
        $(ruleClass5).css('color', '#1e1e1e')
        $(ruleClass5).css(backgroundColorRule, '#fff')
        $('.text-gray2,.text-primary-hover ,.ns-layout__footer, .ns-layout__footer__footer,.hidden-cl ').css('cssText', colorWhiteImportant)
        $('.panel-schedules ,.px-d2 , .ns-session__nav--login ,.ns-layout__footer__footer').css('cssText', backgroundColorDarkImportant)
        $('.ns-tabs ').css(backgroundColorRule, '#1e1e1e')
        $('#linkhome ').css('cssText', colorDarkImportant)
        $('.tablacss ').css('cssText', colorDarkImportant)
        $('.tablacss ').css(backgroundColorRule, '#fff')
        $('.ns-icon-angle-left,.sidepanel-map__card--item__stop ').css('color', '#fff')
        $('.ns-icon-angle-left ').css(backgroundColorRule, '#1e1e1e')
        $('.ns-layout__sidebar__map__menu').css(backgroundColorRule, '#ffffff')
        $('.textomenu ').css('cssText', colorDarkImportant)
        $('.submenuusuario').css(backgroundColorRule, '#ffffff')
        $('.submenuusuario').css('cssText', colorDarkImportant)
        $('.button-phone').css('border', '2px solid #ffffff')
        $('.option-menu').css('border', 'none')
        $('.active').css(backgroundColorRule, 'transparent').css('border', '2px solid #1e1e1e')
      } else {
        $(ruleClass1).css('color', '')
        $(ruleClass1).css(backgroundColorRule, '')
        $(ruleClass2).css('color', '')
        $(ruleClass2).css(backgroundColorRule, '')
        $('a ,.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items , .pb-d6 , .sidepanel-map__card--item__point-name').css('color', '')
        $('.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items ,.pb-d6, .sidepanel-map__card--item__point-name,.flex-middle ,.sidepanel-map__header').css(backgroundColorRule, '')
        $(ruleClass3).css('color', '')
        $(ruleClass3).css(backgroundColorRule, '')
        $('label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title ').css('color', '')
        $('label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title,.ns-session__nav--login ').css(backgroundColorRule, '')
        $('.ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube,.ns-tabs, .button-tabs,.text-primary-hover,.ns-layout__footer__footer,.hidden-cl').css('color', '')
        $('.ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube ,.mb-d1-2 ,.px-d2 ,.ns-tabs__item ,.panel-schedules ,.ns-tabs, .button-tabs,.ns-layout__footer__footer,.hidden-cl').css(backgroundColorRule, '')
        $(' .pb-d6 ,.panel-schedules ,.px-d2 ').css('color', '')
        $(ruleClass4).css('color', '#1e1e1e')
        $(ruleClass4).css(backgroundColorRule, '#fff')
        $(ruleClass5).css('color', '#1e1e1e')
        $(ruleClass5).css(backgroundColorRule, '#fff')
        $('#linkhome').css('color', '')
        $('.textomenu').css('color', '')
        $('.tablacss ').css('color', '')
        $('.tablacss ').css(backgroundColorRule, '')
        $('.ns-icon-angle-left ').css('color', '')
        $('.ns-icon-angle-left ,.colorFondo  ').css(backgroundColorRule, '')
      }
    }
    /* eslint-enable max-len */
  }
})
